import './App.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import Home from './components/Home';
import SummonerDetail from './components/SummonerDetail';
import Header from './components/Header';
import Ladder from './components/Ladder';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import NotFound404 from './components/NotFound404';
import SearchResults from './components/SearchResults';
import ChampionAnalytics from './components/ChampionAnalytics';
import ChampionsIndex from './components/ChampionsIndex';
import MasteryLeaderboard from './components/MasteryLeaderboard';
import ChampionTierList from './components/ChampionTierList';
import axios from 'axios';
import { axiosForAPI } from './helper_functions/utilities';
import {
  formatPlatformLabel,
  MIN_SUMMONER_GAME_NAME_LENGTH,
  PLATFORM_TO_REGION,
  parseSummonerSearchInput,
} from './helper_functions/searchRouting';
import {
  loadRecentSearches,
  mergeRecentSearchEntry,
  saveRecentSearches,
} from './helper_functions/searchHistory';
import { useDeploymentCapabilities } from './context/DeploymentCapabilitiesContext';

function LegacyChampionAnalyticsRedirect() {
  const { championNameId } = useParams()

  return <Navigate replace to={`/analytics/champions/${championNameId}`} />
}

function LegacyMasteryRedirect() {
  const location = useLocation()

  return <Navigate replace to={`/analytics/mastery${location.search}`} />
}

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isPlatformSupported, isReady, retry, status } = useDeploymentCapabilities()



  const [summonerSearchFormData, setSummonerSearchFormData] = useState({
    query: "",
    gameName: "",
    tagLine: "",
  })
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [championSuggestions, setChampionSuggestions] = useState([])
  const [searchSuggestionsLoading, setSearchSuggestionsLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState(() => loadRecentSearches())

  const [formErrors, setFormErrors] = useState([])
  const [summonerDetailError, setSummonerDetailError] = useState(false)


  const [platform, setPlatform] = useState('');
  const [region, setRegion] = useState('');
  const [displayRegion, setDisplayRegion] = useState('')
  // displayRegion is the colloquial way players refer to 'region', it is actually platform without the integer
  const [searchTrigger, setSearchTrigger] = useState(false)
  const searchSuggestionCacheRef = useRef(new Map())
  const searchRequestIdRef = useRef(0)

  useEffect(() => { // If these values are previously saved to local storage, get & save to state
    const storedPlatform = localStorage.getItem('platform')
    const storedRegion = localStorage.getItem('region')
    const storedDisplayRegion = localStorage.getItem('displayRegion')

    if (isReady && storedPlatform && storedRegion && storedDisplayRegion && isPlatformSupported(storedPlatform)) {
      setPlatform(localStorage.getItem('platform'))
      setRegion(localStorage.getItem('region'))
      setDisplayRegion(localStorage.getItem('displayRegion'))
    }
  }, [isPlatformSupported, isReady])

  useEffect(() => {
    saveRecentSearches(recentSearches)
  }, [recentSearches])

  useEffect(() => {
    if (!location.pathname.startsWith('/search/')) {
      return
    }

    const params = new URLSearchParams(location.search)
    const query = params.get('q') || ''
    setSummonerSearchFormData((previousValue) => {
      if (previousValue.query === query) {
        return previousValue
      }

      return parseSummonerSearchInput(query)
    })
  }, [location.pathname, location.search])

  useEffect(() => {
    const requestId = searchRequestIdRef.current + 1
    searchRequestIdRef.current = requestId
    const trimmedQuery = summonerSearchFormData?.query?.trim()

    if (!isReady || !platform || !trimmedQuery) {
      setSearchSuggestions([])
      setChampionSuggestions([])
      setSearchSuggestionsLoading(false)
      return undefined
    }

    const normalizedQuery = trimmedQuery.toLowerCase()
    const cacheKey = `${platform}:${normalizedQuery}`
    const cachedResults = searchSuggestionCacheRef.current.get(cacheKey)

    if (cachedResults) {
      setSearchSuggestions(cachedResults.summoners)
      setChampionSuggestions(cachedResults.champions)
      setSearchSuggestionsLoading(false)
      return undefined
    }

    const parsedQuery = parseSummonerSearchInput(trimmedQuery)
    const shouldSearchSummoners = parsedQuery.gameName.length >= MIN_SUMMONER_GAME_NAME_LENGTH
    const shouldSearchChampions = trimmedQuery.length >= 2

    if (!shouldSearchSummoners && !shouldSearchChampions) {
      setSearchSuggestions([])
      setChampionSuggestions([])
      setSearchSuggestionsLoading(false)
      return undefined
    }

    const abortController = new AbortController()
    const isCurrentRequest = () => (
      searchRequestIdRef.current === requestId && !abortController.signal.aborted
    )
    const debounceTimeout = setTimeout(async () => {
      try {
        if (!isCurrentRequest()) return
        setSearchSuggestionsLoading(true)
        const requests = [
          shouldSearchSummoners
            ? axiosForAPI.get('/summoner-search/', {
            params: {
              platform,
              query: trimmedQuery,
              limit: 6,
            },
            signal: abortController.signal,
          })
            : Promise.resolve({ data: { results: [] } }),
          shouldSearchChampions
            ? axiosForAPI.get('/champion-search/', {
            params: {
              query: trimmedQuery,
              limit: 5,
            },
            signal: abortController.signal,
          })
            : Promise.resolve({ data: { results: [] } }),
        ]

        const [summonerResponse, championResponse] = await Promise.all(requests)
        if (!isCurrentRequest()) return
        const nextSummonerSuggestions = summonerResponse.data?.results || []
        const nextChampionSuggestions = championResponse.data?.results || []

        searchSuggestionCacheRef.current.set(cacheKey, {
          summoners: nextSummonerSuggestions,
          champions: nextChampionSuggestions,
        })

        setSearchSuggestions(nextSummonerSuggestions)
        setChampionSuggestions(nextChampionSuggestions)
      } catch (requestError) {
        if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
          return
        }

        if (!isCurrentRequest()) return

        setSearchSuggestions([])
        setChampionSuggestions([])
      } finally {
        if (isCurrentRequest()) {
          setSearchSuggestionsLoading(false)
        }
      }
    }, 300)

    return () => {
      abortController.abort()
      clearTimeout(debounceTimeout)
    }
  }, [isReady, platform, summonerSearchFormData?.query])

  function addRecentSearch(nextEntry) {
    setRecentSearches((previousEntries) => mergeRecentSearchEntry(previousEntries, nextEntry))
  }

  function handleSummonerNameEntry(event) {
    setSummonerSearchFormData(parseSummonerSearchInput(event.target.value))
  }

  function validateSearch(searchData = summonerSearchFormData) {
    let collectedErrors = []

    if (!platform || !region || !displayRegion) {
      collectedErrors.push('Must select a region')
    }

    if (!searchData?.query?.trim()) {
      collectedErrors.push('Enter a Summoner or Champion Name')
    }

    if (searchData?.query?.trim() && searchData.gameName.length < MIN_SUMMONER_GAME_NAME_LENGTH) {
      collectedErrors.push(`Summoner searches require at least ${MIN_SUMMONER_GAME_NAME_LENGTH} game-name characters`)
    }

    return collectedErrors
  }


  const globallyUpdateDisplayedRegion = useCallback((currentPlatform) => {
    if (!isReady || !currentPlatform) {
      return
    }
    const selectedPlatform = currentPlatform
    const newRegion = PLATFORM_TO_REGION[selectedPlatform]

    setPlatform(selectedPlatform)
    setRegion(newRegion)
    setDisplayRegion(formatPlatformLabel(selectedPlatform).toLowerCase())

    localStorage.setItem('platform', selectedPlatform);
    localStorage.setItem('region', newRegion);
    localStorage.setItem('displayRegion', formatPlatformLabel(selectedPlatform).toLowerCase());
  }, [isReady])

  function handlePlatformSelection(event) {
    const selectedPlatform = event.target.value
    globallyUpdateDisplayedRegion(selectedPlatform)
  }

  function navigateToSummonerProfile({ gameName, tagLine, routeDisplayRegion = displayRegion }) {
    if (!routeDisplayRegion || !gameName || !tagLine) {
      return
    }

    addRecentSearch({
      kind: 'summoner',
      label: `${gameName}#${tagLine}`,
      displayRegion: routeDisplayRegion,
      gameName,
      tagLine,
    })

    setFormErrors([])
    setSummonerSearchFormData(parseSummonerSearchInput(''))
    setSearchTrigger((prev) => !prev)
    navigate(`/summoners/${routeDisplayRegion}/${gameName}-${tagLine}`)
  }

  function navigateToSearchResults(rawQuery, routeDisplayRegion = displayRegion, searchMode = 'players', page = 1) {
    const trimmedQuery = rawQuery?.trim()
    if (!routeDisplayRegion || !trimmedQuery) {
      return
    }

    const normalizedSearchMode = searchMode === 'champions' ? 'champions' : 'players'
    addRecentSearch({
      kind: normalizedSearchMode === 'champions' ? 'champion' : 'search',
      label: trimmedQuery,
      displayRegion: routeDisplayRegion,
      query: trimmedQuery,
      searchMode: normalizedSearchMode,
    })

    setFormErrors([])
    navigate(`/search/${routeDisplayRegion}?q=${encodeURIComponent(trimmedQuery)}&mode=${normalizedSearchMode}${page > 1 ? `&page=${page}` : ''}`)
  }

  function handleSuggestionSelect(result) {
    setSummonerSearchFormData(parseSummonerSearchInput(`${result.game_name}#${result.tag_line}`))
    navigateToSummonerProfile({
      gameName: result.game_name,
      tagLine: result.tag_line,
    })
  }

  function handleChampionPlayersSelect(champion) {
    setSummonerSearchFormData(parseSummonerSearchInput(champion.name))
    navigate(`/analytics/mastery/${encodeURIComponent(champion.name_id || champion.name.toLowerCase())}`)
  }

  function handleChampionAnalyticsSelect(champion) {
    setSummonerSearchFormData(parseSummonerSearchInput(champion.name))
    addRecentSearch({
      kind: 'champion-analytics',
      label: champion.name,
      displayRegion,
      query: champion.name,
      championNameId: champion.name_id,
    })
    navigate(`/analytics/champions/${champion.name_id}`)
  }

  function handleRecentSearchSelect(recentSearch) {
    if (recentSearch.kind === 'summoner') {
      setSummonerSearchFormData(parseSummonerSearchInput(recentSearch.label))
      navigateToSummonerProfile({
        gameName: recentSearch.gameName,
        tagLine: recentSearch.tagLine,
        routeDisplayRegion: recentSearch.displayRegion,
      })
      return
    }

    if (recentSearch.kind === 'champion-analytics') {
      setSummonerSearchFormData(parseSummonerSearchInput(recentSearch.label))
      navigate(`/analytics/champions/${recentSearch.championNameId || recentSearch.query}`)
      return
    }

    setSummonerSearchFormData(parseSummonerSearchInput(recentSearch.label))
    navigateToSearchResults(
      recentSearch.query,
      recentSearch.displayRegion,
      recentSearch.searchMode
    )
  }

  function submitAndSearchSummoner(event) {
    event.preventDefault()

    // Read the submitted form value directly so button submits cannot use a
    // stale controlled-input snapshot.
    const submittedQuery = event.currentTarget?.elements?.gameName?.value
    const searchFormData = submittedQuery === undefined
      ? summonerSearchFormData
      : parseSummonerSearchInput(submittedQuery)

    const potentialErrors = validateSearch(searchFormData)
    const invalid = potentialErrors.length // > 0 (which is truthy) if invalid

    if (!invalid) {
      setFormErrors([])
      if (searchFormData.gameName && searchFormData.tagLine) {
        navigateToSummonerProfile({
          gameName: searchFormData.gameName,
          tagLine: searchFormData.tagLine,
        })
      } else {
        navigateToSearchResults(searchFormData.query, displayRegion)
      }
    } else {
      setFormErrors(potentialErrors)
    }

  }
  return (
    <div className="flex min-h-screen flex-col bg-[#16161b] text-white">
      <Header
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        platform={platform}
        displayRegion={displayRegion}
        handleSummonerNameEntry={handleSummonerNameEntry}
        handlePlatformSelection={handlePlatformSelection}
        submitAndSearchSummoner={submitAndSearchSummoner}
        summonerSearchFormData={summonerSearchFormData}
        championSuggestions={championSuggestions}
        searchSuggestions={searchSuggestions}
        searchSuggestionsLoading={searchSuggestionsLoading}
        recentSearches={recentSearches}
        onSelectChampionAnalytics={handleChampionAnalyticsSelect}
        onSelectChampionPlayers={handleChampionPlayersSelect}
        onSelectRecentSearch={handleRecentSearchSelect}
        onSelectSearchSuggestion={handleSuggestionSelect}
        onSubmitDirectSearch={() => submitAndSearchSummoner({ preventDefault() {} })}
        onViewAllSearchResults={() => navigateToSearchResults(summonerSearchFormData.query, displayRegion)}
      />
      <div className="mx-auto w-full max-w-screen-lg px-4">
        {status === 'retrying' ? (
          <div className="mt-3 rounded-lg border border-amber-800/50 bg-amber-950/20 px-3 py-2 text-center text-xs font-semibold text-amber-200" role="status">
            Initialization failed. Retrying...
          </div>
        ) : null}
        {status === 'failed' ? (
          <div className="mt-3 flex items-center justify-center gap-3 rounded-lg border border-rose-800/60 bg-rose-950/30 px-3 py-2 text-center text-xs font-semibold text-rose-100" role="alert">
            <span>Initialization unavailable. Retry to load regional availability.</span>
            <button className="rounded border border-rose-700/80 px-2 py-1 uppercase tracking-[0.12em] hover:bg-rose-900/40" onClick={retry} type="button">Retry</button>
          </div>
        ) : null}
      </div>
      <main className="flex-1">
        {isReady ? <Routes>
          <Route path="/" exact
            element={
              <Home
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                handleSummonerNameEntry={handleSummonerNameEntry}
                handlePlatformSelection={handlePlatformSelection}
                submitAndSearchSummoner={submitAndSearchSummoner}
                platform={platform}
                region={region}
                summonerSearchFormData={summonerSearchFormData}
                championSuggestions={championSuggestions}
                searchSuggestions={searchSuggestions}
                searchSuggestionsLoading={searchSuggestionsLoading}
                recentSearches={recentSearches}
                onSelectChampionAnalytics={handleChampionAnalyticsSelect}
                onSelectChampionPlayers={handleChampionPlayersSelect}
                onSelectRecentSearch={handleRecentSearchSelect}
                onSelectSearchSuggestion={handleSuggestionSelect}
                onSubmitDirectSearch={() => submitAndSearchSummoner({ preventDefault() {} })}
                onViewAllSearchResults={() => navigateToSearchResults(summonerSearchFormData.query, displayRegion)}
              />
            } />
          <Route path="/contact"
            element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/analytics/champions" element={<ChampionsIndex />} />
          <Route path="/analytics/mastery/:championNameId" element={<MasteryLeaderboard />} />
          <Route path="/analytics/mastery" element={<MasteryLeaderboard />} />
          <Route path="/analytics/tier-list/high-mastery" element={<ChampionTierList />} />
          <Route path="/analytics/tier-list" element={<ChampionTierList />} />
          <Route path="/analytics/champions/:championNameId/:roleId/*" element={<ChampionAnalytics />} />
          <Route path="/analytics/champions/:championNameId" element={<ChampionAnalytics />} />
          <Route path="/champions" element={<Navigate replace to="/analytics/champions" />} />
          <Route path="/mastery" element={<LegacyMasteryRedirect />} />
          <Route path="/tier-list" element={<Navigate replace to="/analytics/tier-list" />} />
          <Route path="/champions/:championNameId" element={<LegacyChampionAnalyticsRedirect />} />
          <Route
            path="/ladder"
            element={<Navigate replace to={`/ladder/${displayRegion || 'na'}/solo/1`} />}
          />
          <Route
            path="/search/:displayRegion"
            element={
              <SearchResults
                globallyUpdateDisplayedRegion={globallyUpdateDisplayedRegion}
                setSummonerSearchFormData={setSummonerSearchFormData}
                navigateToSearchResults={navigateToSearchResults}
              />
            }
          />
          <Route path="/summoners/:displayRegion/:displayNameZipped"
            element={<SummonerDetail  searchTrigger={searchTrigger} region={region} platform={platform} globallyUpdateDisplayedRegion={globallyUpdateDisplayedRegion} error={summonerDetailError} setError={setSummonerDetailError} summonerSearchFormData={summonerSearchFormData} />} />
          <Route path="/summoners/:displayRegion/:displayNameZipped/live"
            element={<SummonerDetail  searchTrigger={searchTrigger} region={region} platform={platform} globallyUpdateDisplayedRegion={globallyUpdateDisplayedRegion} error={summonerDetailError} setError={setSummonerDetailError} summonerSearchFormData={summonerSearchFormData} />} />
          <Route
            path="/ladder/:displayRegion/:ladderType/:pageNumber"
            element={<Ladder globallyUpdateDisplayedRegion={globallyUpdateDisplayedRegion} />}
          />
          <Route path="*" element={<NotFound404 />} />
        </Routes> : null}
      </main>
      <Footer />
    </div>
  )
}

export default App;
